import { City, Job, Prefecture, SelectOptionType } from '../../types/jobTypes'
import { useState } from 'react'
import axios from 'axios';

interface UseCityOptionsReturn {
  cityOption: SelectOptionType;
  cityOptions: SelectOptionType[];
  loadingCities: boolean;
  defaultPrefectureOption: SelectOptionType;
  defaultCityOption: SelectOptionType;
  prefectureOption: SelectOptionType;
  prefectureOptions: SelectOptionType[];
  setCityOption: React.Dispatch<React.SetStateAction<SelectOptionType>>;
  setCityOptions: React.Dispatch<React.SetStateAction<SelectOptionType[]>>;
  setLoadingCities: React.Dispatch<React.SetStateAction<boolean>>;
  setPrefectureOption: React.Dispatch<React.SetStateAction<SelectOptionType>>;
  setPrefectureOptions: React.Dispatch<React.SetStateAction<SelectOptionType[]>>;
  updateCityOptions: (prefectureId: number) => void;
  initializePrefectureOptions: (resPrefectures: Prefecture[]) => void;
  setInitialPrefectureAndCityOption: (currentIsEditMode: Boolean, currentJob: Job) => void;
}

export function useCityOptions (): UseCityOptionsReturn {
  const defaultPrefectureOption: SelectOptionType = {
    value: null,
    label: '都道府県を選択'
  }

  const defaultCityOption: SelectOptionType = {
    value: null,
    label: '市区町村を選択'
  }

  // 都道府県セレクトボックスの選択肢一覧
  const [prefectureOptions, setPrefectureOptions] = useState<SelectOptionType[]>([defaultPrefectureOption])
  // 都道府県セレクトボックスの選択されている値(value, labelを持つ)
  const [prefectureOption, setPrefectureOption] = useState<SelectOptionType>(defaultPrefectureOption)

  // 市区町村セレクトボックスの選択肢一覧
  const [cityOptions, setCityOptions] = useState<SelectOptionType[]>([defaultCityOption])
  // 市区町村セレクトボックスの選択されている値(value, labelを持つ)
  const [cityOption, setCityOption] = useState<SelectOptionType>(defaultCityOption)
  // 市区町村のローディング用の状態管理
  const [loadingCities, setLoadingCities] = useState(false)

  // prefecture_idに紐づく市区町村リストを取得する
  const updateCityOptions = (prefectureId: number): Promise<void> => {
    setLoadingCities(true)
    return axios.get(`/api/v1/prefectures/${prefectureId}/cities`)
      .then (res => {
        const resCites: City[] = JSON.parse(res.data.cities)
        setCityOptions([
          defaultCityOption,
          ...resCites.map(city => ({value: city.id, label: city.name}))
        ])
        setCityOption(defaultCityOption)
      })
      .catch (error => {
        setCityOptions([defaultCityOption])
        setCityOption(defaultCityOption)
      })
      .finally(() => {
        setLoadingCities(false)
      })
  }

  // 初期値としての市区町村を設定する
  const _fetchAndSetCurrentCity = (prefecture_id: number, city_id: number) => {
    axios.get(`/api/v1/prefectures/${prefecture_id}/cities/${city_id}`)
    .then( res => {
      const resCity: SelectOptionType = JSON.parse(res.data.city)
      setCityOption(resCity)
    })
    .catch( error => {
      setCityOption(defaultCityOption)
    })
  }

  // ページのマウント時に初期値としての都道府県一覧を選択肢としてセットする
  const initializePrefectureOptions = (resPrefectures: Prefecture[]): void => {
    setPrefectureOptions([
      defaultPrefectureOption,
      ...resPrefectures.map(prefecture => ({value: prefecture.id, label: prefecture.name}))
    ])
  }

  // 編集時に初期値として都道府県・紐づく市区町村リスト・市区町村をセットする
  const setInitialPrefectureAndCityOption = async (currentIsEditMode: Boolean, currentJob: Job) => {
    if (currentIsEditMode) {
      // currentJobが持っているprefecture_idを選択肢の形に整形する
      const currentPrefectureOption = prefectureOptions
        .find(prefectureOption => prefectureOption.value === currentJob.prefecture_id)
      // 整形したprefectureOptionをセレクトボックスの値にセットする
      currentPrefectureOption
        ? setPrefectureOption(currentPrefectureOption)
        : setPrefectureOption(defaultPrefectureOption)

      // 都道府県に紐づく市区町村リストを読み込む
      if (currentJob.prefecture_id) {
        // updateCityOptions()でcityOptionをdefaultOptionにするためfetchAndSrtCurrentCityの前に実行
        await updateCityOptions (currentJob.prefecture_id)
      } else {
        setCityOptions([defaultCityOption]); // prefecture_id がない場合は市区町村リストをクリア
      }

      if (currentJob.city_id) {
        _fetchAndSetCurrentCity(currentJob.prefecture_id, currentJob.city_id)
      }
    }
  }

  return {
    cityOption,
    cityOptions,
    loadingCities,
    defaultCityOption,
    defaultPrefectureOption,
    prefectureOption,
    prefectureOptions,
    setCityOption,
    setCityOptions,
    setLoadingCities,
    setPrefectureOption,
    setPrefectureOptions,
    updateCityOptions,
    initializePrefectureOptions,
    setInitialPrefectureAndCityOption,
  }
}
